import { useMutation, useQueryClient } from "@tanstack/react-query";
import { NotificationsLevel } from "../../../lib/enums";
import { show } from "../../../notifications/notifications";
import api from "../../../services/api";
import { pluralize } from "../../../utils";
import { IntegrationType } from "../@types";
import { GET_SAMPLE_ANALYSIS_STATUS_QUERY_KEY } from "../components/analysistable/integrations_analysis_table/IntegrationsAnalysisTable";
import { ANALYSIS_STATUS_QUERY_KEY } from "./useGetSampleAnalysisStatus";

const INITIAL_PAYLOAD = {
  samples: [],
  tc: false,
  ti_cloud: false,
  cape: false,
  cuckoo: false,
  fireeye: false,
  joe: false,
  rl_auxiliary_analysis: false,
  vmray_tcbase: false,
  dynamic_analysis: []
};

type Sample = {
  hash_value?: string;
  source_id?: string | number;
  relationship_id?: string | number;
};

type Payload = {
  integrations: Array<IntegrationType>;
  samples: Array<Sample>;
  platform?: string;
};

const createPayload = (payload: Payload) => {
  const integrations = payload.integrations.reduce((acc, curr) => {
    if (curr === "rl_dynamic_analysis") {
      return {
        ...acc,
        dynamic_analysis: [...acc.dynamic_analysis, "rl_dynamic_analysis"],
        rl_cloud_sandbox_platform: payload.platform
      };
    }
    return {
      ...acc,
      [curr]: true,
      dynamic_analysis: ["tc", "ti_cloud"].includes(curr)
        ? acc.dynamic_analysis
        : [...acc.dynamic_analysis, curr]
    };
  }, INITIAL_PAYLOAD);

  return {
    ...integrations,
    samples: payload.samples
  };
};

const useReanalyze = () => {
  const queryClient = useQueryClient();

  const mutationFn = (payload: Payload) => {
    const _payload = createPayload(payload);
    return api.reanalyze(_payload);
  };
  const { mutate } = useMutation({
    mutationFn: (payload: Payload) => mutationFn(payload),
    onSuccess: (_, variables) => {
      show({
        level: NotificationsLevel.SUCCESS,
        message: `Succesfully sent ${pluralize(
          "sample",
          variables.integrations.length
        )} to analysis.`
      });
      queryClient.refetchQueries({ queryKey: [ANALYSIS_STATUS_QUERY_KEY] });
      queryClient.refetchQueries({
        queryKey: [GET_SAMPLE_ANALYSIS_STATUS_QUERY_KEY]
      });
    },
    onError: (_, variables) => {
      show({
        level: NotificationsLevel.ERROR,
        message: `Failed to send ${pluralize(
          "sample",
          variables.integrations.length
        )} to analysis.`
      });
    }
  });

  return { mutate };
};

export default useReanalyze;
